/**
 * Tab list following the WAI-ARIA tabs pattern.
 *
 * Panels are rendered by the tab blocks themselves. Before this script runs,
 * CSS shows only the first panel, so the content is usable without JavaScript.
 */

/**
 * Inner SVG markup for each tab icon, keyed by the block's icon setting.
 * These are static, trusted constants (never merchant input), so injecting them
 * with innerHTML is safe. Drawn on a 24x24 viewBox, stroke-based.
 * @type {Record<string, string>}
 */
const TAB_ICONS = {
  description:
    '<line x1="5" y1="7" x2="19" y2="7"/><line x1="5" y1="12" x2="19" y2="12"/><line x1="5" y1="17" x2="13" y2="17"/>',
  specs:
    '<circle cx="5" cy="7" r="1"/><circle cx="5" cy="12" r="1"/><circle cx="5" cy="17" r="1"/><line x1="9" y1="7" x2="19" y2="7"/><line x1="9" y1="12" x2="19" y2="12"/><line x1="9" y1="17" x2="19" y2="17"/>',
  documents:
    '<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/>',
  info:
    '<circle cx="12" cy="12" r="9"/><line x1="12" y1="11" x2="12" y2="16"/><line x1="12" y1="8" x2="12" y2="8.01"/>',
  warranty:
    '<path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z"/><path d="m9 12 2 2 4-4"/>',
  delivery:
    '<path d="M3 6h11v9H3z"/><path d="M14 9h4l3 3v3h-7z"/><circle cx="7" cy="18" r="1.6"/><circle cx="17" cy="18" r="1.6"/>',
  tools:
    '<path d="M14.7 6.3a3.5 3.5 0 0 0-4.6 4.2L4 16.6 7.4 20l6.1-6.1a3.5 3.5 0 0 0 4.2-4.6l-2.3 2.3-2-2z"/>',
  gallery:
    '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9.5" r="1.6"/><path d="m4 18 5-4 4 3 3-2 4 3"/>',
  support:
    '<path d="M4 13a8 8 0 0 1 16 0"/><rect x="3" y="13" width="4" height="6" rx="1.5"/><rect x="17" y="13" width="4" height="6" rx="1.5"/>',
};

class ProductTabs extends HTMLElement {
  /** @type {HTMLElement | null} */
  list = null;
  /** @type {HTMLElement[]} */
  panels = [];
  /** @type {HTMLButtonElement[]} */
  tabs = [];

  connectedCallback() {
    this.list = this.querySelector('[role="tablist"]');
    this.panels = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[role="tabpanel"]')));

    if (!this.list || this.panels.length === 0) return;

    this.buildTabs();
    this.tabs = /** @type {HTMLButtonElement[]} */ (Array.from(this.list.querySelectorAll('[role="tab"]')));

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.handleClick);
      tab.addEventListener('keydown', this.handleKeydown);
    });

    // Signals to CSS that panel visibility is now driven by the active class.
    this.classList.add('tabs--ready');
    this.select(0);
  }

  /** Creates one button per panel, labelled from the panel's data-tab-label. */
  buildTabs() {
    if (!this.list) return;
    this.list.textContent = '';

    this.panels.forEach((panel, index) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'tabs__tab';
      tab.setAttribute('role', 'tab');
      tab.id = `tab-${panel.id.replace(/^panel-/, '')}`;
      tab.setAttribute('aria-controls', panel.id);

      const iconName = panel.dataset.tabIcon;
      if (iconName && TAB_ICONS[iconName]) {
        const icon = document.createElement('span');
        icon.className = 'tabs__tab-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">${TAB_ICONS[iconName]}</svg>`;
        tab.appendChild(icon);
      }

      const label = document.createElement('span');
      label.className = 'tabs__tab-label';
      label.textContent = panel.dataset.tabLabel || `${index + 1}`;
      tab.appendChild(label);

      this.list.appendChild(tab);
    });
  }

  handleClick = (event) => {
    this.select(this.tabs.indexOf(event.currentTarget));
  };

  handleKeydown = (event) => {
    const current = this.tabs.indexOf(event.currentTarget);
    let next = null;

    if (event.key === 'ArrowRight') next = (current + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') next = (current - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.tabs.length - 1;

    if (next === null) return;

    event.preventDefault();
    this.select(next);
    this.tabs[next].focus();
  };

  select(index) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
      tab.classList.toggle('is-active', active);
    });

    this.panels.forEach((panel, i) => {
      panel.classList.toggle('is-active', i === index);
    });
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
